import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
  LucideIcon,
  User,
  LayoutGrid,
  BookOpen,
  GraduationCap,
  UserRound,
  Magnet,
  Plus,
  Pencil,
  Trash2,
  Download,
  RefreshCw,
  ArrowRight,
  Sparkles,
} from 'lucide-react';

import {
  PaletteEntity,
  PaletteEntityKind,
  popupState,
  searchState,
} from 'core';
import {
  SearchNames,
  useEsignAvailability,
  usePalette,
  useGlobalHotkey,
  useGoToKeys,
  usePaletteSearch,
  usePopupStateOperations,
  useStudentPermissions,
  useUser,
  visibleFor,
  GOTO_TARGETS,
} from 'core/hooks';
import {
  useActivePageActions,
  isPaletteAction,
  IPageAction,
  PageActionCategory,
} from 'context';
import { UserRole } from 'types';
import { aiKbShowsReviewEntry } from 'core/hooks/AiKnowledge/aiKnowledgeModel';
import { useAiKbReviewerMe } from 'core/hooks/AiKnowledge/useAiKbReview';
import { matchesAltLetter, formatAltHotkey } from 'core/utils/altHotkey';

import {
  IPaletteRowItem,
  IPaletteRunContext,
  IPaletteSection,
} from './paletteTypes';
import { buildCommands, scoreCommand, scoreText } from './commands';
import { buildEntityActions, openActionFor, IActionGate } from './actions';
import { CommandPalette as CommandPaletteView } from './CommandPalette';
import { ICheatSheetSection } from './ShortcutsCheatSheet';

/** `⌘` on Apple platforms, `Ctrl` elsewhere — for the cheat sheet's key caps. */
const MODIFIER_LABEL =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
    ? '⌘'
    : 'Ctrl';

const COMMANDS_LIMIT = 7;

/**
 * Safety cap on the inline per-entity action buttons rendered on a result row.
 * The strip drops the primary «open» (that is the row-body click) and shows the
 * remaining quick actions; no entity currently exceeds this, so nothing is
 * capped in practice — the limit only guards a future overflow, which stays
 * reachable via the →/Tab drill-in.
 */
const MAX_INLINE_ACTIONS = 6;

const ENTITY_ICONS: Record<PaletteEntityKind, LucideIcon> = {
  student: User,
  group: LayoutGrid,
  course: BookOpen,
  teacher: GraduationCap,
  user: UserRound,
  // Задача #116: тот же знак, что у пункта «Лиды» в меню и в разделе.
  lead: Magnet,
};

/** Icon fallback for a page action that declares a category but no explicit icon. */
const CATEGORY_ICONS: Record<PageActionCategory, LucideIcon> = {
  create: Plus,
  update: Pencil,
  delete: Trash2,
  download: Download,
  sync: RefreshCw,
  navigate: ArrowRight,
  other: Sparkles,
};

const pageActionIcon = (action: IPageAction): LucideIcon =>
  action.icon ?? (action.category ? CATEGORY_ICONS[action.category] : Sparkles);

/**
 * Smart container for the command palette. Owns the global hotkey, palette
 * state, the debounced multi-entity search, the command/action registries, the
 * flat keyboard cursor and focus / scroll-lock lifecycle. Mounted once in
 * `RootRouter`; renders nothing until opened.
 */
export const CommandPalette = memo(() => {
  const { t } = useTranslation();
  const history = useHistory();
  const [{ user }] = useUser();
  const permissions = useStudentPermissions();
  const {
    operations: { showPopup },
  } = usePopupStateOperations();

  const {
    open,
    query,
    mode,
    activeEntity,
    selectedIndex,
    toggle,
    openHelp,
    closePalette,
    setQuery,
    enterEntityActions,
    back,
    setSelectedIndex,
  } = usePalette();

  useGlobalHotkey(toggle, openHelp);
  useGoToKeys();

  const roles = useMemo(() => (user?.roles ?? []) as UserRole[], [user]);
  const { results, loading, hasQuery } = usePaletteSearch(query, roles);

  const activePageActions = useActivePageActions();

  // The page actions surfaced in the palette (all but pure keyboard-nav keys).
  const palettePageActions = useMemo(
    () => activePageActions.filter(isPaletteAction),
    [activePageActions],
  );

  // Lookup for the in-open Alt+letter path: press `Alt`+a page action's letter
  // (Option+letter on macOS) to run it, regardless of the query text. Keyed by
  // the lowercase letter; matched via `event.code` in `onKeyDown`.
  const pageActionKeyMap = useMemo(() => {
    const map = new Map<string, IPageAction>();
    palettePageActions.forEach((action) => {
      if (action.key) map.set(action.key.toLowerCase(), action);
    });
    return map;
  }, [palettePageActions]);

  const inputRef = useRef<HTMLInputElement>(null);

  const setSettingsSearch = useCallback((value: string) => {
    searchState({ ...searchState(), [SearchNames.settings]: value });
  }, []);

  const ctx = useMemo<IPaletteRunContext>(
    () => ({ history, showPopup, setSettingsSearch, query, t }),
    [history, showPopup, setSettingsSearch, query, t],
  );

  const gate = useMemo<IActionGate>(() => ({ roles, permissions, t }), [
    roles,
    permissions,
    t,
  ]);

  const { enabled: esignEnabled } = useEsignAvailability();
  // ТЗ 0118 B4: команда очереди базы знаний — как пункт меню.
  const { me: kbReviewer } = useAiKbReviewerMe(roles.length === 0);
  const kbReview = aiKbShowsReviewEntry(kbReviewer);
  const commands = useMemo(
    () => buildCommands(roles, t, { esign: esignEnabled, kbReview }),
    [roles, t, esignEnabled, kbReview],
  );
  const rankedCommands = useMemo(
    () =>
      commands
        .map((command) => ({ command, score: scoreCommand(query, command) }))
        .filter((entry) => entry.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, COMMANDS_LIMIT)
        .map((entry) => entry.command),
    [commands, query],
  );

  const sections = useMemo<IPaletteSection[]>(() => {
    const result: IPaletteSection[] = [];

    // «На этой странице» — the current page's primary actions from the ONE
    // page-action registry. Shown at the top on the empty-query landing view,
    // and (label-filtered) folded into search matches when the operator types.
    const trimmedQuery = query.trim().toLowerCase();
    const pageItems = palettePageActions
      .filter(
        (action) =>
          !trimmedQuery ||
          scoreText(trimmedQuery, action.label.toLowerCase()) > 0,
      )
      .map<IPaletteRowItem>((action) => ({
        key: `page-${action.id}`,
        icon: pageActionIcon(action),
        title: action.label,
        keyBadge: action.key ? formatAltHotkey(action.key) : undefined,
        activate: () => {
          closePalette();
          action.run();
        },
      }));
    if (pageItems.length) {
      result.push({
        key: 'page',
        title: t('commandPalette.sections.page'),
        items: pageItems,
      });
    }

    if (rankedCommands.length) {
      result.push({
        key: 'commands',
        title: t('commandPalette.sections.commands'),
        items: rankedCommands.map((command) => ({
          key: `command-${command.id}`,
          icon: command.icon,
          title: command.title,
          hint: t('commandPalette.hint.run'),
          activate: () => {
            closePalette();
            command.run(ctx);
          },
        })),
      });
    }

    if (hasQuery) {
      const toItem = (
        entity: PaletteEntity,
        sectionKey: string,
      ): IPaletteRowItem => ({
        key: `${sectionKey}-${entity.id}`,
        icon: ENTITY_ICONS[entity.kind],
        title: entity.title,
        subtitle: entity.subtitle,
        hint: t('commandPalette.hint.actions'),
        activate: () => {
          closePalette();
          openActionFor(entity, t).run(ctx);
        },
        entity,
        // Inline quick-action buttons: the entity's actions minus index 0 (the
        // primary «open», already the row-body click). Each `run` is bound to
        // close-then-dispatch, exactly like the drill-in rows.
        actions: buildEntityActions(entity, gate)
          .slice(1)
          .slice(0, MAX_INLINE_ACTIONS)
          .map((action) => ({
            id: action.id,
            icon: action.icon,
            title: action.title,
            run: () => {
              closePalette();
              action.run(ctx);
            },
          })),
      });

      const addSection = (
        key: string,
        titleKey: string,
        entities: PaletteEntity[],
      ) => {
        if (entities.length) {
          result.push({
            key,
            title: t(titleKey),
            items: entities.map((entity) => toItem(entity, key)),
          });
        }
      };

      addSection(
        'students',
        'commandPalette.sections.students',
        results.students,
      );
      addSection('groups', 'commandPalette.sections.groups', results.groups);
      addSection('courses', 'commandPalette.sections.courses', results.courses);
      addSection(
        'teachers',
        'commandPalette.sections.teachers',
        results.teachers,
      );
      addSection('users', 'commandPalette.sections.users', results.users);
      addSection('leads', 'commandPalette.sections.leads', results.leads);
    }

    return result;
  }, [
    palettePageActions,
    query,
    rankedCommands,
    hasQuery,
    results,
    ctx,
    closePalette,
    gate,
    t,
  ]);

  const flatItems = useMemo(
    () => sections.flatMap((section) => section.items),
    [sections],
  );

  const actionItems = useMemo<IPaletteRowItem[]>(() => {
    if (mode !== 'entityActions' || !activeEntity) return [];
    return buildEntityActions(activeEntity, gate).map((action) => ({
      key: `action-${action.id}`,
      icon: action.icon,
      title: action.title,
      hint: action.hint,
      activate: () => {
        closePalette();
        action.run(ctx);
      },
    }));
  }, [mode, activeEntity, gate, ctx, closePalette]);

  const listLength =
    mode === 'entityActions' ? actionItems.length : flatItems.length;

  // Keep the cursor within the (async-changing) list bounds.
  useEffect(() => {
    if (selectedIndex > 0 && selectedIndex >= listLength) {
      setSelectedIndex(listLength > 0 ? listLength - 1 : 0);
    }
  }, [selectedIndex, listLength, setSelectedIndex]);

  // Focus the input, lock body scroll, and restore both on close. Overflow is
  // handed back to `GlobalModal` when the palette closes straight into a popup.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflowY;
    document.body.style.overflowY = 'hidden';
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(timer);
      const popups = popupState();
      const anyPopupOpen = (Object.keys(popups) as Array<
        keyof typeof popups
      >).some((key) => popups[key]);
      if (!anyPopupOpen) {
        document.body.style.overflowY = prevOverflow || 'auto';
      }
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  }, [open]);

  // The cheat sheet renders no input, so Escape can't ride the input's
  // `onKeyDown` — close it via a scoped document listener while help is open.
  useEffect(() => {
    if (!open || mode !== 'help' || typeof document === 'undefined') {
      return undefined;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePalette();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, mode, closePalette]);

  // Role-gated cheat-sheet sections: global keys, `g → section` chords, and the
  // active page's contextual keys (read live from the PageShortcuts registry).
  const helpSections = useMemo<ICheatSheetSection[]>(() => {
    const gotoRows = GOTO_TARGETS.filter((target) =>
      visibleFor(roles, target.allowed),
    ).map((target) => ({
      keys: ['g', target.secondKey.toUpperCase()],
      label: t(target.labelKey),
    }));

    return [
      {
        key: 'global',
        title: t('commandPalette.help.sections.global'),
        rows: [
          {
            keys: [MODIFIER_LABEL, 'K'],
            label: t('commandPalette.help.openPalette'),
          },
          { keys: ['?'], label: t('commandPalette.help.title') },
        ],
      },
      {
        key: 'goto',
        title: t('commandPalette.help.sections.goto'),
        hint: t('commandPalette.help.gotoHint'),
        rows: gotoRows,
      },
      {
        key: 'page',
        title: t('commandPalette.help.sections.page'),
        rows: activePageActions
          .filter((action) => Boolean(action.key))
          .map((action) => ({
            keys: [formatAltHotkey(action.key as string)],
            label: action.label,
          })),
        emptyText: t('commandPalette.help.noPageShortcuts'),
      },
    ];
  }, [roles, activePageActions, t]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // `Alt`+letter (Option+letter on macOS) runs a page action even with the
      // palette open and text already in the query — a modifier chord never
      // collides with typing. Matched via `event.code` so macOS Option+letter
      // (which mutates `event.key`) still resolves to the right letter. The same
      // chord works when the palette is CLOSED via the PageShortcuts dispatcher.
      const altAction = Array.from(pageActionKeyMap).find(([letter]) =>
        matchesAltLetter(event.nativeEvent, letter),
      );
      if (altAction) {
        event.preventDefault();
        closePalette();
        altAction[1].run();
        return;
      }

      const list = mode === 'entityActions' ? actionItems : flatItems;
      const len = list.length;

      // Wrapping move over the currently-visible list — shared by ↑/↓ and the
      // Ctrl+N / Ctrl+P aliases so the two never diverge.
      const moveSelection = (delta: number) => {
        if (len) setSelectedIndex((selectedIndex + delta + len) % len);
      };

      // Ctrl+N / Ctrl+P mirror ArrowDown / ArrowUp while the palette is open,
      // in BOTH search and entityActions modes. Guard against Cmd/Alt so we do
      // not hijack OS chords; preventDefault suppresses the browser's
      // new-window / print defaults while the palette owns the keyboard.
      if (event.ctrlKey && !event.metaKey && !event.altKey) {
        const key = event.key.toLowerCase();
        if (key === 'n') {
          event.preventDefault();
          moveSelection(1);
          return;
        }
        if (key === 'p') {
          event.preventDefault();
          moveSelection(-1);
          return;
        }
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          moveSelection(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          moveSelection(-1);
          break;
        case 'Enter':
          event.preventDefault();
          if (list[selectedIndex]) list[selectedIndex].activate();
          break;
        case 'Tab':
        case 'ArrowRight': {
          if (mode === 'search') {
            const item = flatItems[selectedIndex];
            if (item && item.entity) {
              event.preventDefault();
              enterEntityActions(item.entity);
              break;
            }
          }
          if (event.key === 'Tab') event.preventDefault();
          break;
        }
        case 'ArrowLeft':
          if (mode === 'entityActions') {
            event.preventDefault();
            back();
          }
          break;
        case 'Escape':
          event.preventDefault();
          if (mode === 'entityActions') back();
          else closePalette();
          break;
        default:
          break;
      }
    },
    [
      mode,
      pageActionKeyMap,
      actionItems,
      flatItems,
      selectedIndex,
      setSelectedIndex,
      enterEntityActions,
      back,
      closePalette,
    ],
  );

  if (!open) return null;

  const footer =
    mode === 'help' ? (
      <FooterHint>
        <Kbd>esc</Kbd>
        {t('commandPalette.footer.close')}
      </FooterHint>
    ) : (
      <>
        <FooterHint>
          <Kbd>↑↓</Kbd>
          {t('commandPalette.footer.navigate')}
        </FooterHint>
        <FooterHint>
          <Kbd>↵</Kbd>
          {t('commandPalette.footer.select')}
        </FooterHint>
        <FooterHint>
          <Kbd>→</Kbd>
          {t('commandPalette.footer.actions')}
        </FooterHint>
        <FooterHint>
          <Kbd>esc</Kbd>
          {t('commandPalette.footer.close')}
        </FooterHint>
      </>
    );

  return (
    <CommandPaletteView
      query={query}
      placeholder={t('commandPalette.placeholder')}
      onQueryChange={setQuery}
      onKeyDown={onKeyDown}
      mode={mode}
      sections={sections}
      actionItems={actionItems}
      actionsTitle={activeEntity ? activeEntity.title : ''}
      backHint={t('commandPalette.hint.back')}
      selectedIndex={selectedIndex}
      onHover={setSelectedIndex}
      loading={loading}
      emptyText={
        hasQuery ? t('commandPalette.noResults') : t('commandPalette.empty')
      }
      footer={footer}
      hasContent={sections.length > 0}
      onOverlayMouseDown={closePalette}
      inputRef={inputRef}
      helpTitle={t('commandPalette.help.title')}
      helpSections={helpSections}
      onClose={closePalette}
      closeLabel={t('commandPalette.footer.close')}
      onBack={back}
      backLabel={t('mobile.back')}
    />
  );
});

const FooterHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Kbd = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border: 1px solid var(--k-border-dfe6f1);
  border-radius: 4px;
  font-size: 11px;
  color: var(--k-fg-616e78);
  background: var(--k-bg-fafbfd);
`;
